import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, ClipboardCheck, UserCheck, ArrowRightCircle, Plus, RotateCcw, CalendarClock, History, SlidersHorizontal, Pencil, Pill } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, fmtUS, fmtTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, PastelCard, Select, DataTable, Tabs } from "@/components/common";
import { AssignmentDialog, STATUSES } from "@/components/rotation/AssignmentDialog";
import { RosterSettings } from "@/components/rotation/RosterSettings";
import { RotationHistory } from "@/components/rotation/RotationHistory";
import { OpenedAssignment } from "@/components/rotation/OpenedAssignment";
import { RotationMedRequestDialog } from "@/components/rotation/RotationMedRequestDialog";

function RotationBoard({ board }) {
  return (
    <div className="grid grid-cols-1 gap-x-3 overflow-hidden rounded-lg border border-blue-100 bg-white text-sm dark:border-white/10 dark:bg-transparent sm:grid-cols-2 lg:grid-flow-col lg:grid-cols-4 lg:grid-rows-4" data-testid="rotation-board">
      {board.map((b, i) => (
        <div key={b.label} title={!b.user_id ? "No matching employee account" : b.account_name !== b.label ? `Account: ${b.account_name}` : undefined}
          className={`flex items-center justify-between gap-2 px-3 py-1.5 ${i % 2 === 0 ? "bg-[#EEF5FF] dark:bg-white/5" : ""}`} data-testid={`board-row-${i}`}>
          <span className={`min-w-0 truncate font-medium ${b.user_id ? "text-[var(--ink)]" : "italic text-[var(--muted-ink)]"}`} data-testid={`board-name-${i}`}>{b.label}</span>
          <span className="shrink-0 font-bold tabular-nums text-[#20336F] dark:text-blue-200" data-testid={`board-count-${i}`}>{b.user_id ? b.count : "—"}</span>
        </div>
      ))}
    </div>
  );
}

function ActivityLog({ st, canEdit, canRequest, onEdit, onRequest, onChange }) {
  const setStatus = async (a, status) => {
    try { await api.put(`/rotation/assignments/${a.id}`, { status }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DataTable testid="activity-table" rows={st.assignments} empty="No patients recorded in today's rotation yet." columns={[
      { key: "local_time", label: "Time", render: (r) => fmtTime(r.local_time) }, { key: "testing_date", label: "Testing Date", render: (r) => fmtUS(r.testing_date || r.local_date) },
      { key: "athena_id", label: "Athena ID", render: (r) => <span className="font-semibold">{r.athena_id}</span> },
      { key: "service", label: "Service", render: (r) => <>{r.service}{r.custom_service && <span className="ml-1 text-[10px] text-[var(--muted-ink)]">(custom)</span>}</> },
      { key: "employee_name", label: "Employee", render: (r) => <>{r.employee_name}{r.override && <span className="ml-1 text-[10px] text-amber-700" title="Manual override">override</span>}</> },
      { key: "location_name", label: "Location" },
      { key: "status", label: "Status", render: (r) => <Select className="h-8 w-36" value={r.status} onChange={(v) => setStatus(r, v)} options={STATUSES} testid={`status-${r.id}`} /> },
      { key: "actions", label: "Actions", sortable: false, render: (r) => (
        <div className="flex flex-wrap gap-1">
          {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => onEdit(r)} data-testid={`edit-assignment-${r.id}`}><Pencil size={12} />Edit</button>}
          {canRequest && <button className="btn btn-ghost btn-sm text-[#283F91] dark:text-blue-300" onClick={() => onRequest(r)} data-testid={`request-med-${r.id}`}><Pill size={12} />Request Medication</button>}
        </div>) },
    ]} />
  );
}

export default function TestingRotation() {
  const { user, can } = useApp();
  const [locs, setLocs] = useState([]);
  const [lid, setLid] = useState(localStorage.getItem("rotLoc") || "");
  const [st, setSt] = useState(null);
  const [tab, setTab] = useState("today");
  const [dlg, setDlg] = useState(null);
  const [reset, setReset] = useState(false);
  const [mrFor, setMrFor] = useState(null);
  const canRequest = can("action:med_request_manage") && can("page:medication_requests");
  const canDesk = can("action:testing_assign"), canManage = can("action:rotation_manage");
  useEffect(() => {
    api.get("/rotation/locations").then((r) => {
      setLocs(r.data);
      if (!r.data.some((l) => l.id === lid)) setLid(r.data[0]?.id || "");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    if (!lid) return;
    localStorage.setItem("rotLoc", lid);
    api.get("/rotation/state", { params: { location_id: lid } }).then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [lid]);
  useEffect(() => { load(); const i = setInterval(load, 60000); return () => clearInterval(i); }, [load]);
  const doReset = async () => {
    try { await api.post("/rotation/reset", { location_id: lid, confirm: true }); toast.success("New daily rotation started"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tabs = [{ key: "today", label: "Daily Rotation", icon: CalendarClock }, { key: "history", label: "Rotation History", icon: History },
    ...(canManage ? [{ key: "settings", label: "Rotation Settings", icon: SlidersHorizontal }] : [])];
  return (
    <div>
      <OpenedAssignment />
      <PageHeader title="Patient Testing Rotation" subtitle={st ? `Rotation for ${fmtUS(st.day.date)} · resets daily at 8:45 AM Eastern · now ${fmtTime(st.now_et)} ET` : "Daily patient testing assignments by employee and location"}
        actions={<>
          <Select className="w-60" value={lid} onChange={setLid} testid="rotation-location-select" options={locs.map((l) => ({ value: l.id, label: l.name }))} />
          {canDesk && st && <button className="btn btn-primary" onClick={() => setDlg({})} data-testid="add-patient-btn"><Plus size={15} />Add Patient</button>}
          {user.role === "admin" && st && <button className="btn btn-ghost" onClick={() => setReset(true)} data-testid="manual-reset-btn"><RotateCcw size={14} />Reset Rotation</button>}
        </>} />
      {!locs.length && <div className="panel p-10 text-sm text-[var(--muted-ink)]" data-testid="no-locations">You are not assigned to any location. Ask an administrator to assign your location.</div>}
      {st && (
        <>
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="rotation-cards">
            <PastelCard icon={Users} tone="blue" label="Patients Today" value={st.summary.patients_today} hint="Unique Athena IDs" testid="card-patients-today" />
            <PastelCard icon={ClipboardCheck} tone="green" label="Services Today" value={st.summary.services_today} hint="Excludes cancelled" testid="card-services-today" />
            <PastelCard icon={UserCheck} tone="lavender" label="Employees on Rotation" value={st.summary.employees_on_rotation} hint="Participating today" testid="card-employees-rotation" />
            <PastelCard icon={ArrowRightCircle} tone="amber" label="Next Employee" value={st.next_employee?.name || "None eligible"} hint="Suggested for the next patient" testid="card-next-employee" />
          </div>
          <Tabs tabs={tabs} value={tab} onChange={setTab} testid="rotation-tabs" />
          {tab === "today" && (
            <div className="space-y-4">
              {st.board && <section><h2 className="mb-1.5 text-base font-semibold text-[#20336F] dark:text-blue-200 md:text-lg">Employee Rotation <span className="text-xs font-normal text-[var(--muted-ink)]">patients assigned today</span></h2>
                <RotationBoard board={st.board} /></section>}
              <section><h2 className="mb-2 text-base font-semibold text-[#20336F] dark:text-blue-200 md:text-lg">Daily Activity Log</h2>
                <ActivityLog st={st} canEdit={canDesk || canManage} canRequest={canRequest} onEdit={(a) => setDlg({ edit: a })} onRequest={setMrFor} onChange={load} /></section>
            </div>
          )}
          {tab === "history" && <RotationHistory locations={locs} st={st} />}
          {tab === "settings" && canManage && <RosterSettings st={st} onSaved={load} />}
        </>
      )}
      <AssignmentDialog open={!!dlg} onClose={() => setDlg(null)} st={st} edit={dlg?.edit} canEditAll={canDesk || canManage} onSaved={load} />
      <RotationMedRequestDialog assignment={mrFor} onClose={() => setMrFor(null)} />
      <AlertDialog open={reset} onOpenChange={setReset}>
        <AlertDialogContent data-testid="reset-dialog">
          <AlertDialogHeader><AlertDialogTitle>Start a new daily rotation?</AlertDialogTitle>
            <AlertDialogDescription>Today's rotation for {st?.location?.name} will be closed and archived. No records are deleted; counters and rotation positions restart. This action is recorded in the audit log.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={doReset} data-testid="reset-confirm">Reset rotation</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
